import { useEffect, useRef } from "react";
import { t } from "@lingui/core/macro";
import { i18n } from "@app/i18n";
import { toast } from "@app/ui";
import { useOnlineStatus } from "@/hooks/UseOnlineStatus";

/**
 * 오프라인일 때 persistent 토스트로 안내한다. 예전엔 화면 상단 고정 바였으나
 * 못생겨서 토스트로 옮겼다. duration: Infinity 라 자동으로 사라지지 않고,
 * 사용자가 직접 닫거나(X/스와이프) 온라인으로 복귀하면 사라진다.
 *
 * App.tsx는 로그인/온보딩 등 여러 조기 반환 분기를 갖고 있어 그 안에 두면 일부
 * 화면에서 누락되므로, 모든 화면에 공통으로 보이도록 main.tsx에서 <App/>과
 * 형제로 렌더한다(렌더 결과는 없고 effect만 수행).
 */
export function OfflineToast() {
  const isOnline = useOnlineStatus();
  // 현재 떠 있는 오프라인 토스트의 id. 온라인 복귀 시 이 id로 닫는다.
  const toastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isOnline) {
      // 온라인으로 돌아오면 오프라인 안내는 더 필요 없으니 우리가 띄운 토스트를 닫는다.
      // 사용자가 이미 직접 닫았어도 dismiss 는 매칭되는 id 가 없으면 no-op 이라 안전하다.
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }
    // 이미 떠 있으면(사용자가 아직 안 닫음) 중복으로 띄우지 않는다.
    if (toastIdRef.current) return;
    toastIdRef.current = toast.warning(
      t(i18n)`오프라인 — 변경 사항은 연결되면 자동으로 동기화됩니다.`,
      // Infinity: 자동으로 안 사라지고 사용자가 직접 닫기 전까지 유지된다.
      { duration: Infinity }
    );
  }, [isOnline]);

  return null;
}
